import DueDatePicker from "@/components/DueDatePicker/dueDatePicker.component";
import DueDateRangePicker from "@/components/DueDatePicker/dueDateRangePicker.component";
import { Button } from "@/shadcn/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import { Input } from "@/shadcn/components/ui/input";
import { DateRangeSearch, DateType, WithinDate } from "@/types";
import { formatDueDate } from "@/utils/utils";
import { ChevronsUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";

type DatePickerSearchProps = {
  datePickerRef: React.RefObject<HTMLDivElement>;
  dateType: DateType;
  setDateType: React.Dispatch<React.SetStateAction<DateType>>;
  dateData: WithinDate | DateRangeSearch | Date | string | null;
  setDateData: React.Dispatch<
    React.SetStateAction<WithinDate | DateRangeSearch | Date | string | null>
  >;
  onDateTypeChange: (
    dateType: DateType,
    data: WithinDate | DateRangeSearch | Date | string | null
  ) => void;
};

const DatePickerSearch: React.FC<DatePickerSearchProps> = ({
  datePickerRef,
  onDateTypeChange,
  dateType,
  setDateType,
  dateData,
  setDateData,
}) => {
  const [timePeriods, setTimePeriods] = useState<
    "Days" | "Weeks" | "Months" | null
  >(null);

  // For within the last and within the next
  const [timeUnit, setTimeUnit] = useState<number | null>(null);

  const dateTypes: Array<
    | "Any Date"
    | "Specific Date"
    | "Yesterday"
    | "Today"
    | "Tomorrow"
    | "Overdue"
  > = [
    "Any Date",
    "Overdue",
    "Specific Date",
    "Yesterday",
    "Today",
    "Tomorrow",
  ];

  const withinItems: Array<"Days" | "Weeks" | "Months"> = [
    "Days",
    "Weeks",
    "Months",
  ];

  // Range
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const [specificDate, setSpecificDate] = useState<Date | null | undefined>(
    null
  );

  function handleDueDateRangeChange(
    fromDate: Date | null,
    toDate: Date | null
  ) {
    console.log("fromDate", fromDate);
    console.log("toDate", toDate);
    const formattedFromDate = formatDueDate(fromDate);
    const formattedToDate = formatDueDate(toDate);

    const rangeData: DateRangeSearch = {
      fromDate: formattedFromDate,
      toDate: formattedToDate,
    };

    if (toDate && fromDate) {
      console.log("Triggered");
      onDateTypeChange(dateType, rangeData);
    }
  }

  function handleDueDateSpecificChange(date: Date | undefined | null) {
    setSpecificDate(date);
    console.log("specificDate", date);
    onDateTypeChange(dateType, date ?? null);
  }

  useEffect(() => {
    console.log("fromDate", fromDate);
    console.log("toDate", toDate);
    console.log("dateType", dateType);
    console.log("timePeriods", timePeriods);
    console.log("dateData", dateData);
  }, [fromDate, toDate, dateType, timePeriods, dateData]);

  useEffect(() => {
    // If dateType is Specific Date, check dateData, if exists, update specificDate
    if (dateType === "Specific Date") {
      if (dateData) {
        if (typeof dateData === "string") {
          setSpecificDate(new Date(dateData));
        } else {
          setSpecificDate(null);
        }
      }
    }

    // If dateType is Within the last or Within the next, check dateData, if exists, update timeUnit and timePeriods
    if (dateType === "Within the last" || dateType === "Within the next") {
      console.log("dateType", dateType);
      console.log("dateData", dateData);
      if (dateData) {
        const data = dateData as WithinDate;
        console.log("data", data);
        console.log("data.timePeriods", data.timePeriods);
        setTimeUnit(data.timeUnit);
        setTimePeriods(data.timePeriods);
      }
    }

    // If dateType is Date Range, check dateData, if exists, update fromDate and toDate
    if (dateType === "Date Range") {
      if (dateData) {
        const data = dateData as DateRangeSearch;
        setFromDate(data.fromDate ? new Date(data.fromDate) : null);
        setToDate(data.toDate ? new Date(data.toDate) : null);
      }
    }

    if (
      dateType === "Any Date" ||
      dateType === "Yesterday" ||
      dateType === "Today" ||
      dateType === "Tomorrow" ||
      dateType === "Overdue"
    ) {
      setSpecificDate(null);
      setTimeUnit(null);
      setTimePeriods(null);
      setFromDate(null);
      setToDate(null);
      setDateData(null);
    }
  }, [dateData, dateType, setDateData]);

  // Resets time unit and time periods when date type changes
  // Resets from date and to date when date type changes
  useEffect(() => {
    if (dateType !== "Within the last" && dateType !== "Within the next") {
      setTimeUnit(null);
      setTimePeriods(null);
    }

    if (dateType !== "Date Range") {
      setFromDate(null);
      setToDate(null);
    }
  }, [dateType]);

  return (
    <div ref={datePickerRef} className="w-full">
      {/* Dropdown */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="justify-between w-full">
            {dateType ?? "Any Date"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-56"
          ref={datePickerRef}
          side="bottom"
          align="end"
        >
          <DropdownMenuGroup>
            {dateTypes.map((type, index) => (
              <React.Fragment key={type}>
                <DropdownMenuItem
                  onClick={() => {
                    setDateType(type);
                  }}
                >
                  {type}
                </DropdownMenuItem>
                {(index === 0 || index === 1 || index === 2) && (
                  <DropdownMenuSeparator />
                )}
              </React.Fragment>
            ))}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {/* Within the last */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger unselectable="on">
                Within the last
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent ref={datePickerRef}>
                  {withinItems.map((item) => (
                    <DropdownMenuItem
                      key={item}
                      onClick={() => {
                        setDateType("Within the last");
                        setTimePeriods(item);
                      }}
                    >
                      {item}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            {/* Within the next */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger unselectable="on">
                Within the next
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent ref={datePickerRef}>
                  {withinItems.map((item) => (
                    <DropdownMenuItem
                      key={item}
                      onClick={() => {
                        setDateType("Within the next");
                        setTimePeriods(item);
                      }}
                    >
                      {item}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setDateType("Date Range");
              }}
            >
              Date Range
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Specific Picker */}
      {dateType === "Specific Date" && (
        <div className="pt-3">
          <DueDatePicker
            dueDate={specificDate}
            setDueDate={setSpecificDate}
            onDateChange={handleDueDateSpecificChange}
            modal={false}
          />
        </div>
      )}

      {/* Within Picker */}
      {(dateType === "Within the last" || dateType === "Within the next") && (
        <div className="grid grid-cols-[2fr_1fr] pt-3 gap-2">
          <Input
            id="edit-day-unit"
            min={1}
            value={timeUnit ?? ""}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                const numericValue =
                  value === "" ? "" : Math.max(1, Number(value));
                setTimeUnit(numericValue === "" ? null : numericValue);
                onDateTypeChange(dateType, {
                  timePeriods,
                  timeUnit: numericValue === "" ? null : numericValue,
                });
              }
            }}
            onBlur={(e) => {
              if (
                e.target.value === "" ||
                Number(e.target.value) < 1 ||
                isNaN(Number(e.target.value))
              ) {
                setTimeUnit(1);
                onDateTypeChange(dateType, { timePeriods, timeUnit: 1 });
              }
            }}
          />
          <label
            className="items-center flex justify-center"
            htmlFor="edit-day-unit"
          >
            {timePeriods}
          </label>
        </div>
      )}

      {/* Range Picker */}
      {dateType === "Date Range" && (
        <div ref={datePickerRef} className="pt-3">
          <DueDateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            onDateChange={handleDueDateRangeChange}
            modal={false}
            rangePickerRef={datePickerRef}
          />
        </div>
      )}
    </div>
  );
};

export default DatePickerSearch;
